import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, Flame, Activity, HelpCircle, Type, Brain, Gamepad2 } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { SignInModal } from '@/features/auth/SignInModal';
import { useStartSoloGame } from '@/features/games/_framework/api';
import { listMiniGames } from '@/features/games/_framework/registry';
import { GAMES_WITH_DAILY } from '@/features/games/_framework/daily';
import { Card, CardDescription, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';
const CATEGORIES = {
    sport: {
        label: 'Sport & motricité',
        description: 'Réflexes, timing, coordination — les épreuves physiques aux JO version pad.',
        icon: Activity,
        accent: 'text-electric-300',
    },
    quiz: {
        label: 'Quiz & culture',
        description: 'Histoire des JO, palmarès, géo, observation — testez votre culture sportive.',
        icon: HelpCircle,
        accent: 'text-flame-300',
    },
    word: {
        label: 'Mots, lettres & mental',
        description: 'Frappe, anagrammes, calcul mental — pour les athlètes du clavier.',
        icon: Type,
        accent: 'text-royal-300',
    },
    memory: {
        label: 'Mémoire & dessin',
        description: 'Séquences à reproduire, paires à retrouver, dessins à réaliser.',
        icon: Brain,
        accent: 'text-gold',
    },
};
/** Ordre d'affichage des sections (priorité visuelle). */
const SECTION_ORDER = ['sport', 'quiz', 'word', 'memory'];
export function GamesPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();
    const startSolo = useStartSoloGame();
    const games = listMiniGames();
    const [query, setQuery] = useState('');
    const [activeCat, setActiveCat] = useState('all');
    const [signInOpen, setSignInOpen] = useState(false);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return games.filter((g) => {
            if (activeCat !== 'all' && g.category !== activeCat)
                return false;
            if (!q)
                return true;
            return g.name.toLowerCase().includes(q) || g.description.toLowerCase().includes(q);
        });
    }, [games, query, activeCat]);
    const grouped = useMemo(() => {
        const map = new Map();
        for (const g of filtered) {
            const arr = map.get(g.category) ?? [];
            arr.push(g);
            map.set(g.category, arr);
        }
        return map;
    }, [filtered]);
    const totalCounts = useMemo(() => {
        const map = new Map();
        for (const g of games) {
            map.set(g.category, (map.get(g.category) ?? 0) + 1);
        }
        return map;
    }, [games]);
    async function startGame(gameType, gameMode) {
        if (!user) {
            setSignInOpen(true);
            return;
        }
        try {
            const res = await startSolo.mutateAsync({ gameType, mode: gameMode });
            navigate(`/game/${res.gameId}`);
        }
        catch (err) {
            const msg = err.message ?? '';
            if (msg.toLowerCase().includes('daily_not_ready')) {
                toast({
                    tone: 'warning',
                    title: 'Daily indisponible',
                    description: "Le défi du jour n'a pas encore été généré pour ce mini-jeu.",
                });
            }
            else {
                toast({ tone: 'error', title: 'Échec', description: msg });
            }
        }
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "space-y-8", children: [_jsx("header", { className: "flex flex-wrap items-end justify-between gap-3", children: _jsxs("div", { children: [_jsxs(Badge, { variant: "electric", size: "lg", children: [games.length, " jeux disponibles"] }), _jsxs("h1", { className: "mt-2 font-display text-4xl uppercase tracking-tight text-ink-50 md:text-5xl", children: [_jsx(Gamepad2, { className: "mr-2 inline h-8 w-8 text-electric-300" }), "Catalogue des jeux"] }), _jsx("p", { className: "mt-1 text-ink-300", children: "Lance une partie en solo pour t'entra\u00EEner, en class\u00E9 pour grimper au leaderboard, ou tente le daily pour te mesurer aux autres sur la m\u00EAme seed." })] }) }), _jsxs("div", { className: "space-y-3", children: [_jsx(Input, { value: query, onChange: (e) => setQuery(e.target.value), placeholder: "Recherche par nom (ex. typing, quiz, surf\u2026)", leftIcon: _jsx(Search, { className: "h-4 w-4" }) }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsx(FilterPill, { label: `Tous (${games.length})`, active: activeCat === 'all', onClick: () => setActiveCat('all') }), SECTION_ORDER.map((cat) => {
                                        const meta = CATEGORIES[cat];
                                        const count = totalCounts.get(cat) ?? 0;
                                        if (count === 0)
                                            return null;
                                        return (_jsx(FilterPill, { label: `${meta.label} (${count})`, active: activeCat === cat, onClick: () => setActiveCat(cat), icon: _jsx(meta.icon, { className: "h-3.5 w-3.5" }) }, cat));
                                    })] })] }), filtered.length === 0 ? (_jsxs(Card, { padding: "lg", className: "text-center", children: [_jsx(CardTitle, { children: "Aucun jeu trouv\u00E9" }), _jsx(CardDescription, { className: "mt-2", children: "Essaie un autre mot-cl\u00E9 ou d\u00E9sactive les filtres." }), (query || activeCat !== 'all') && (_jsx(Button, { variant: "ghost", size: "sm", className: "mt-4", onClick: () => { setQuery(''); setActiveCat('all'); }, children: "R\u00E9initialiser" }))] })) : (_jsx("div", { className: "space-y-10", children: SECTION_ORDER.map((cat) => {
                            const items = grouped.get(cat);
                            if (!items || items.length === 0)
                                return null;
                            const meta = CATEGORIES[cat];
                            return (_jsxs("section", { className: "space-y-3", children: [_jsxs("header", { className: "flex flex-wrap items-baseline justify-between gap-2", children: [_jsxs("h2", { className: cn('font-display text-2xl uppercase tracking-tight', meta.accent), children: [_jsx(meta.icon, { className: "mr-2 inline h-5 w-5" }), meta.label, _jsxs("span", { className: "ml-2 text-sm font-normal text-ink-400", children: ["(", items.length, activeCat === 'all' ? '' : ` sur ${totalCounts.get(cat)}`, ")"] })] }), _jsx("p", { className: "hidden text-xs text-ink-400 md:block", children: meta.description })] }), _jsx("div", { className: "grid gap-3 md:grid-cols-2 lg:grid-cols-3", children: items.map((g) => (_jsx(SoloGameCard, { emoji: g.emoji ?? '🎮', name: g.name, description: g.description, hasDaily: GAMES_WITH_DAILY.has(g.type), isPending: startSolo.isPending, onTrain: () => void startGame(g.type, 'training'), onRanked: () => void startGame(g.type, 'ranked'), onDaily: () => void startGame(g.type, 'daily') }, g.type))) })] }, cat));
                        }) }))] }), _jsx(SignInModal, { open: signInOpen, onOpenChange: setSignInOpen })] }));
}
// ────────────────────────────────────────────────────────────────────────────
// Composants internes
// ────────────────────────────────────────────────────────────────────────────
function FilterPill({ label, active, onClick, icon, }) {
    return (_jsxs("button", { type: "button", onClick: onClick, className: cn('inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold uppercase tracking-wider transition-colors', active
            ? 'bg-electric-500/30 text-electric-100 ring-2 ring-electric-400'
            : 'bg-ink-900/60 text-ink-300 ring-1 ring-ink-700 hover:bg-ink-800'), children: [icon, label] }));
}
function SoloGameCard({ emoji, name, description, hasDaily, isPending, onTrain, onRanked, onDaily, }) {
    return (_jsxs(Card, { padding: "md", className: "flex flex-col", children: [_jsxs("div", { className: "flex flex-1 items-start gap-3", children: [_jsx("span", { className: "grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-ink-800 text-2xl", children: emoji }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx(CardTitle, { className: "text-base", children: name }), _jsx(CardDescription, { className: "mt-1 text-xs", children: description })] })] }), _jsxs("div", { className: "mt-3 grid grid-cols-2 gap-2", children: [_jsx(Button, { variant: "secondary", size: "sm", onClick: onTrain, isLoading: isPending, children: "Entra\u00EEnement" }), _jsx(Button, { variant: "primary", size: "sm", onClick: onRanked, isLoading: isPending, leftIcon: _jsx(Flame, { className: "h-3.5 w-3.5" }), children: "Class\u00E9" }), hasDaily && (_jsx(Button, { variant: "flame", size: "sm", onClick: onDaily, isLoading: isPending, leftIcon: _jsx(Calendar, { className: "h-3.5 w-3.5" }), className: "col-span-2", children: "Daily du jour" }))] })] }));
}
